<template>
  <div class="enhanced-autocomplete">
    <!-- Search input field -->
    <div class="search-container">
      <input
        type="text"
        class="form-control autocomplete-input custom-input"
        :placeholder="placeholder"
        v-model="searchText"
        @input="handleInput"
        @focus="showResults = true"
        ref="searchInput"
      />
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <span class="spinner"></span>
      </div>
    </div>
    
    <!-- Results dropdown -->
    <div class="results-container" v-if="showResults && filteredResults.length > 0">
      <div class="autocomplete-result-list">
        <div 
          v-for="(result, index) in filteredResults" 
          :key="`result-${index}`"
          class="autocomplete-result"
          @click="toggleSelection(result)"
        >
          <label class="checkbox-container">
            <input
              type="checkbox"
              :checked="isSelected(result)"
              @click.stop
              @change="toggleSelection(result)"
            >
            <span class="checkbox-label">{{ result }}</span>
          </label>
        </div>
      </div>
    </div>
    
    <!-- No results message -->
    <div class="results-container" v-if="showResults && searchText && filteredResults.length === 0">
      <div class="autocomplete-result-list">
        <div class="autocomplete-result no-results">
          No results found
        </div>
      </div>
    </div>
    
    <!-- Multi-select tags display - only shown in multi-select mode -->
    <div v-if="isMultiSelect && hasValues" class="selected-tags mt-2">
      <div v-for="(item, index) in normalizedValues" :key="`tag-${index}`" class="tag">
        {{ item }}
        <span class="tag-close" @click="removeTag(index)">×</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EnhancedAutocomplete',
  props: {
    // Function that returns search results
    search: {
      type: Function,
      required: true
    },
    // Current value (string for single select, array for multi-select)
    value: {
      type: [String, Array, Number],
      default: null
    },
    // Whether to allow multiple selections (true) or just a single value (false)
    isMultiSelect: {
      type: Boolean,
      default: false
    },
    // Custom placeholder text
    placeholder: {
      type: String,
      default: 'Search...'
    },
    // Debounce time for autocomplete
    debounceTime: {
      type: Number,
      default: 200
    },
    fieldType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchText: '',
      results: [],
      showResults: false,
      debounceTimer: null,
      selectedItems: [],
      isLoading: false,
      internalValue: this.value // Track internal value to sync with prop
    }
  },
  computed: {
    hasValues() {
      return this.normalizedValues.length > 0;
    },
    seperator() {
      // Check for both singular and plural versions to be safe
      return this.fieldType === 'mobile_os_hardware_model' || 
             this.fieldType === 'mobile_os_hardware_models' ? ';' : ',';
    },
    normalizedValues() {
      // Use internalValue to ensure reactivity
      if (!this.internalValue) return [];
      if (Array.isArray(this.internalValue)) return this.internalValue;
      if (typeof this.internalValue === 'string' && this.isMultiSelect && this.internalValue.includes(this.seperator)) {
        const values = this.internalValue.split(this.seperator).map(item => item.trim()).filter(Boolean);
        return values;
      }
      return [this.internalValue];
    },
    filteredResults() {
      // Don't filter out selected items to allow deselection directly from suggestion rows
      return this.results;
    }
  },
  created() {
    // Add click event listener to close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  mounted() {
    // Set initial value in the input field
    this.initializeValue();
  },
  beforeDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    // Watch for changes in the value prop to update the search text
    value: {
      handler(newVal) {
        this.internalValue = newVal; // Keep internal value in sync
        this.initializeValue();
      },
      immediate: true
    },
    isMultiSelect: {
      handler() {
        this.initializeValue();
      }
    },
    // Watch internal value changes to update computed normalizedValues
    internalValue: {
      handler(newVal) {
        // This watch is just to ensure reactivity
      },
      immediate: true
    }
  },
  methods: {
    initializeValue() {
      // For single-select mode, display the value in the input field
      if (!this.isMultiSelect && this.value) {
        this.searchText = this.value;
      } else if (this.isMultiSelect && !this.hasValues) {
        this.searchText = '';
      }
    },
    async handleInput() {
      // Show loading indicator
      this.isLoading = true;
      
      // Clear existing timer
      clearTimeout(this.debounceTimer);
      
      // Set new timer
      this.debounceTimer = setTimeout(async () => {
        if (this.searchText.trim() === '') {
          this.results = [];
          this.isLoading = false;
          return;
        }
        
        try {
          this.results = await this.search(this.searchText);
        } catch (error) {
          console.error('Error fetching search results:', error);
          this.results = [];
        } finally {
          this.isLoading = false;
        }
      }, this.debounceTime);
    },
    
    // Update both events and internal state
    updateValue(newValue) {
      this.internalValue = newValue;
      this.$emit('input', newValue);
      this.$emit('update:value', newValue);
    },
    
    toggleSelection(result) {
      if (this.isMultiSelect) {
        if (this.isSelected(result)) {
          this.removeSelection(result);
        } else {
          this.addSelection(result);
        }
      } else {
        // Single select mode
        this.updateValue(result);
        this.searchText = result;
        this.showResults = false;
      }
    },
    
    isSelected(result) {
      return this.normalizedValues.includes(result);
    },
    
    addSelection(result) {
      // Create a copy of the current value to work with
      let newValue;
      
      if (!this.internalValue || this.internalValue === '') {
        // First selection - just use the result directly
        newValue = result;
      } else if (Array.isArray(this.internalValue)) {
        // For backward compatibility, handle array input
        const updatedValues = [...this.internalValue];
        if (!updatedValues.includes(result)) {
          updatedValues.push(result);
        }
        // Always output a string even if input was array
        newValue = updatedValues.join(this.seperator);
      } else if (typeof this.internalValue === 'string') {
        // Handle string input
        let values = this.internalValue.includes(this.seperator) 
          ? this.internalValue.split(this.seperator).map(v => v.trim()).filter(Boolean)
          : [this.internalValue];
          
        if (!values.includes(result)) {
          values.push(result);
        }
        
        // Create string with the appropriate separator
        newValue = values.join(this.seperator);
      }
      
      // Update both internal state and emit events
      this.updateValue(newValue);
      
      // Don't clear search text in multi-select mode to allow selecting multiple items
      // Only set search text in single-select mode
      if (!this.isMultiSelect) {
        this.searchText = result;
      }
    },
    
    removeSelection(result) {
      const values = this.normalizedValues.filter(item => item !== result);
      const outputValue = values.length > 0 ? values.join(this.seperator) : '';
      this.updateValue(outputValue);
    },
    
    removeTag(index) {
      let values = [...this.normalizedValues];
      values.splice(index, 1);
      // Always output as comma-separated string
      const outputValue = values.length > 0 ? values.join(this.seperator) : '';
      this.updateValue(outputValue);
    },
    
    handleClickOutside(event) {
      const autocomplete = this.$el;
      if (autocomplete && !autocomplete.contains(event.target)) {
        this.showResults = false;
      }
    },
    
    setValue(value) {
      if (Array.isArray(value)) {
        // Empty input for arrays since tags will be displayed
        this.searchText = '';
      } else if (value) {
        // For single value, display it in the input
        this.searchText = value;
      } else {
        this.searchText = '';
      }
    }
  }
}
</script>

<style scoped>
.enhanced-autocomplete {
  width: 100%;
  position: relative;
}

.search-container {
  position: relative;
  width: 100%;
}

/* Override the default autocomplete input styles */
.custom-input {
  background-image: none !important;
  padding: 0.5rem 12px !important;
  color: #000 !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: none;
  caret-color: #000;
}

/* Loading indicator */
.loading-indicator {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-left-color: #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.results-container {
  position: absolute;
  width: 100%;
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
}

.autocomplete-result-list {
  margin: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 0;
  box-sizing: border-box;
  background: #fff;
  list-style: none;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.16);
  border-radius: 0 0 8px 8px;
}

.autocomplete-result {
  cursor: pointer;
  padding: 10px 12px;
  color: #000;
  background-image: none !important;
}

.autocomplete-result:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.no-results {
  color: #666;
  font-style: italic;
}

.checkbox-container {
  display: flex;
  align-items: center;
  margin: 0;
  cursor: pointer;
  width: 100%;
}

.checkbox-label {
  margin-left: 8px;
  flex: 1;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  display: inline-flex;
  align-items: center;
  background-color: #e2e8f0;
  color: #4a5568;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.875rem;
}

.tag-close {
  margin-left: 4px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
}

.tag-close:hover {
  color: #e53e3e;
}
</style> 